export const MONTHS = [
    { key: 'janeiro',   label: 'Jan' },
    { key: 'fevereiro', label: 'Fev' },
    { key: 'março',     label: 'Mar' },
    { key: 'abril',     label: 'Abr' },
    { key: 'maio',      label: 'Mai' },
    { key: 'junho',     label: 'Jun' },
    { key: 'julho',     label: 'Jul' },
    { key: 'agosto',    label: 'Ago' },
    { key: 'setembro',  label: 'Set' },
    { key: 'outubro',   label: 'Out' },
    { key: 'novembro',  label: 'Nov' },
    { key: 'dezembro',  label: 'Dez' },
];

const MESES_KEYS = MONTHS.map(m => m.key);

const MESES_POR_SEMESTRE = {
    1: MESES_KEYS.slice(0, 6),
    2: MESES_KEYS.slice(6, 12),
};

const MESES_POR_TRIMESTRE = {
    1: MESES_KEYS.slice(0, 3),
    2: MESES_KEYS.slice(3, 6),
    3: MESES_KEYS.slice(6, 9),
    4: MESES_KEYS.slice(9, 12),
};

// Último dia de cada mês em que um período (ANO/SEMESTRE/TRIMESTRE) pode terminar.
const ULTIMO_DIA_MES_FIM_PERIODO = { março: 31, junho: 30, setembro: 30, dezembro: 31 };

// Prazo (em dias corridos) após o último dia do período em que ainda é permitido criar/reenviar sua importação.
export const PRAZO_CADASTRO_DIAS = 20;

export const TIPO_PERIODO_OPTIONS = [
    { value: 'ANO', label: '12 meses', sub: 'Ano completo' },
    { value: 'SEMESTRE', label: '6 meses', sub: 'Semestre' },
    { value: 'TRIMESTRE', label: '3 meses', sub: 'Trimestre' },
];

/**
 * Opções de periodoNumero para um tipoPeriodo — vazio para ANO (não há sub-escolha).
 *
 * @param {'ANO'|'SEMESTRE'|'TRIMESTRE'} tipoPeriodo
 * @returns {{value: string, label: string, sub: string}[]}
 */
export function getPeriodoNumeroOptions(tipoPeriodo) {
    if (tipoPeriodo === 'SEMESTRE') {
        return [
            { value: '1', label: '1º Semestre', sub: 'Jan – Jun' },
            { value: '2', label: '2º Semestre', sub: 'Jul – Dez' },
        ];
    }
    if (tipoPeriodo === 'TRIMESTRE') {
        return [
            { value: '1', label: '1º Trimestre', sub: 'Jan – Mar' },
            { value: '2', label: '2º Trimestre', sub: 'Abr – Jun' },
            { value: '3', label: '3º Trimestre', sub: 'Jul – Set' },
            { value: '4', label: '4º Trimestre', sub: 'Out – Dez' },
        ];
    }
    return [];
}

/**
 * Array de meses (chaves de MONTHS) de um período.
 *
 * @param {'ANO'|'SEMESTRE'|'TRIMESTRE'} tipoPeriodo
 * @param {number|string} [periodoNumero] - ignorado para ANO
 * @returns {string[]}
 */
export function getMesesDoPeriodo(tipoPeriodo, periodoNumero) {
    if (tipoPeriodo === 'ANO') return MESES_KEYS;
    if (tipoPeriodo === 'SEMESTRE') return MESES_POR_SEMESTRE[Number(periodoNumero)] || [];
    if (tipoPeriodo === 'TRIMESTRE') return MESES_POR_TRIMESTRE[Number(periodoNumero)] || [];
    return [];
}

/**
 * Rótulo curto de um período (sem o ano), ex: "1º Trimestre", "2º Semestre", "Ano Completo".
 *
 * @param {'ANO'|'SEMESTRE'|'TRIMESTRE'} tipoPeriodo
 * @param {number|string} [periodoNumero]
 * @returns {string}
 */
export function formatPeriodoLabel(tipoPeriodo, periodoNumero) {
    if (tipoPeriodo === 'ANO') return 'Ano Completo';
    if (tipoPeriodo === 'SEMESTRE') return `${periodoNumero}º Semestre`;
    if (tipoPeriodo === 'TRIMESTRE') return `${periodoNumero}º Trimestre`;
    return tipoPeriodo || '';
}

/**
 * Data-limite para cadastro de um período: seu último dia + PRAZO_CADASTRO_DIAS dias corridos.
 *
 * @param {'ANO'|'SEMESTRE'|'TRIMESTRE'} tipoPeriodo
 * @param {number|string} [periodoNumero] - ignorado para ANO
 * @param {number|string} ano
 * @returns {Date|null} null se os parâmetros ainda não permitem calcular o período
 */
export function getPrazoCadastro(tipoPeriodo, periodoNumero, ano) {
    if (!ano) return null;
    const meses = getMesesDoPeriodo(tipoPeriodo, periodoNumero);
    if (meses.length === 0) return null;

    const mesFinal = meses[meses.length - 1];
    const mesIndex = MESES_KEYS.indexOf(mesFinal);
    const prazo = new Date(Number(ano), mesIndex, ULTIMO_DIA_MES_FIM_PERIODO[mesFinal]);
    prazo.setDate(prazo.getDate() + PRAZO_CADASTRO_DIAS);
    return prazo;
}

/**
 * Indica se ainda é possível cadastrar/reenviar o período hoje (hoje <= prazo de cadastro).
 * Retorna true quando o período ainda não pode ser calculado (parâmetros incompletos).
 *
 * @param {'ANO'|'SEMESTRE'|'TRIMESTRE'} tipoPeriodo
 * @param {number|string} [periodoNumero]
 * @param {number|string} ano
 * @returns {boolean}
 */
export function isPeriodoDentroDoPrazo(tipoPeriodo, periodoNumero, ano) {
    const prazo = getPrazoCadastro(tipoPeriodo, periodoNumero, ano);
    if (!prazo) return true;

    const hoje = new Date();
    hoje.setHours(0, 0, 0, 0);
    prazo.setHours(0, 0, 0, 0);
    return hoje <= prazo;
}

const numerosIguais = (a, b) =>
    (a == null && b == null) || (a != null && b != null && Number(a) === Number(b));

/**
 * Entre os períodos ativos informados (ex: retorno de ForecastApiService.listImportacoes),
 * encontra um cujos meses se sobreponham ao período dado — mesma lógica de
 * ForecastService#validarSemSobreposicao (backend). Reenviar exatamente o mesmo período
 * (mesmo tipoPeriodo + periodoNumero) não conta como conflito.
 *
 * @param {'ANO'|'SEMESTRE'|'TRIMESTRE'} tipoPeriodo
 * @param {number|string} [periodoNumero]
 * @param {{tipo_periodo: string, periodo_numero: number|null}[]} periodosAtivos
 * @returns {{tipo_periodo: string, periodo_numero: number|null}|null}
 */
export function encontrarPeriodoConflitante(tipoPeriodo, periodoNumero, periodosAtivos) {
    const mesesNovo = new Set(getMesesDoPeriodo(tipoPeriodo, periodoNumero));

    for (const periodo of periodosAtivos || []) {
        const ehMesmoPeriodo = periodo.tipo_periodo === tipoPeriodo
            && numerosIguais(periodo.periodo_numero, periodoNumero);
        if (ehMesmoPeriodo) continue;

        const mesesExistente = getMesesDoPeriodo(periodo.tipo_periodo, periodo.periodo_numero);
        if (mesesExistente.some(mes => mesesNovo.has(mes))) return periodo;
    }
    return null;
}

/**
 * Entre os períodos ativos informados, encontra o que é EXATAMENTE o mesmo período
 * informado (mesmo tipoPeriodo + periodoNumero) — usado para avisar o usuário que
 * salvar vai substituir (desativar) essa importação, sem bloquear o fluxo.
 *
 * @param {'ANO'|'SEMESTRE'|'TRIMESTRE'} tipoPeriodo
 * @param {number|string} [periodoNumero]
 * @param {{tipo_periodo: string, periodo_numero: number|null}[]} periodosAtivos
 * @returns {Object|null}
 */
export function encontrarPeriodoExato(tipoPeriodo, periodoNumero, periodosAtivos) {
    return (periodosAtivos || []).find(periodo =>
        periodo.tipo_periodo === tipoPeriodo && numerosIguais(periodo.periodo_numero, periodoNumero)
    ) || null;
}
